import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { adminApi } from "@/lib/adminApi";
import { toast } from "sonner";
import { Plus, Search, RefreshCw, Trees, MapPin, ChevronRight, Loader2, Pencil, Upload } from "lucide-react";
import { FormDialog, Field, PhotoUploader } from "./AssetForms";
import CsvImportDialog from "./CsvImportDialog";

/* Parks list — operational register. Table-first for speed; click a row to
   open the Park Record. */

const STATUS_OPTIONS = ["", "ACTIVE", "INACTIVE"];

export default function Parks() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [operator, setOperator] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);  // null = closed; {} = create; object = edit
  const [importing, setImporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.q = search;
      if (operator) params.operator = operator;
      if (status) params.status = status;
      const { data } = await adminApi.get("/assets/parks", { params });
      setRows(data.items || []);
    } finally { setLoading(false); }
  }, [search, operator, status]);

  useEffect(() => { load(); }, [load]);

  // Unique operator names for the filter dropdown.
  const operatorOptions = Array.from(new Set(rows.map((r) => r.operator_name).filter(Boolean))).sort();

  return (
    <div data-testid="assets-parks-page">
      <div className="admin-page-header flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="eyebrow">Assets · Parks Register</p>
          <h1>Parks</h1>
          <p className="lead">Operational directory of every park PHS serves. Click a row to open the record.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setImporting(true)} className="text-sm h-10 px-4 rounded-md border border-[#E2DCD0] text-[#071A33] hover:border-[#0F766E] hover:text-[#0F766E] inline-flex items-center gap-1.5" data-testid="parks-import-btn">
            <Upload size={14}/> Bulk import CSV
          </button>
          <button onClick={() => setEditing({})} className="text-sm h-10 px-4 rounded-md bg-[#064E2E] text-white hover:bg-[#0a5b55] inline-flex items-center gap-1.5" data-testid="parks-add-btn">
            <Plus size={14}/> Add Park
          </button>
          <button onClick={load} className="btn-outline h-10 py-0 px-3" data-testid="parks-refresh"><RefreshCw size={14} className={loading ? "animate-spin" : ""}/></button>
        </div>
      </div>

      {/* Filters */}
      <div className="admin-panel p-3 mb-4 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 flex-1 min-w-[220px]">
          <Search size={14} className="text-[#475569]"/>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by park name…" className="field-input m-0 h-9 text-sm flex-1" data-testid="parks-search"/>
        </div>
        <select value={operator} onChange={(e) => setOperator(e.target.value)} className="field-input h-9 text-sm m-0" data-testid="parks-operator-filter">
          <option value="">All operators</option>
          {operatorOptions.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="field-input h-9 text-sm m-0" data-testid="parks-status-filter">
          {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s || "All statuses"}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="admin-panel overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[10px] tracking-[0.18em] uppercase text-[#475569] bg-[#F7F4ED]/60">
            <tr className="text-left">
              <th className="px-4 py-3 w-[80px]">Photo</th>
              <th className="px-4 py-3">Park</th>
              <th className="px-4 py-3">Operator</th>
              <th className="px-4 py-3">Location</th>
              <th className="px-4 py-3 text-right">Plots</th>
              <th className="px-4 py-3 text-right">Open jobs</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E2DCD0]">
            {rows.length === 0 && !loading && (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-[#475569] italic">No parks yet. Click "Add Park" to create one.</td></tr>
            )}
            {rows.map((p) => (
              <tr key={p.id} className="hover:bg-[#F7F4ED]/50 cursor-pointer" data-testid={`parks-row-${p.id}`}>
                <td className="px-4 py-3" onClick={() => window.location.assign(`/adminpanel/assets/parks/${p.id}`)}>
                  <ParkThumb src={p.photo_url}/>
                </td>
                <td className="px-4 py-3 font-medium text-[#071A33]" onClick={() => window.location.assign(`/adminpanel/assets/parks/${p.id}`)}>{p.name}</td>
                <td className="px-4 py-3 text-[#475569]" onClick={() => window.location.assign(`/adminpanel/assets/parks/${p.id}`)}>{p.operator_name}</td>
                <td className="px-4 py-3 text-[#475569]" onClick={() => window.location.assign(`/adminpanel/assets/parks/${p.id}`)}>
                  <span className="inline-flex items-center gap-1"><MapPin size={11}/>{[p.town, p.county, p.postcode].filter(Boolean).join(", ") || "—"}</span>
                </td>
                <td className="px-4 py-3 text-right tabular-nums">{p.plot_count}</td>
                <td className="px-4 py-3 text-right tabular-nums">
                  <span className={p.open_jobs > 0 ? "text-[#B88A2A] font-semibold" : "text-[#475569]"}>{p.open_jobs}</span>
                </td>
                <td className="px-4 py-3">
                  <StatusPill status={p.status}/>
                </td>
                <td className="px-4 py-3 text-right">
                  <button className="text-[12px] text-[#475569] hover:text-[#064E2E] mr-2" onClick={(e) => { e.stopPropagation(); setEditing(p); }} data-testid={`parks-edit-${p.id}`}>
                    <Pencil size={13}/>
                  </button>
                  <Link to={`/adminpanel/assets/parks/${p.id}`} className="text-[12px] px-2 py-1 bg-[#0F766E] text-white hover:bg-[#0a5b55] rounded inline-flex items-center gap-1" data-testid={`parks-open-${p.id}`}>
                    Open <ChevronRight size={11}/>
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {loading && <div className="px-4 py-6 text-center text-[#475569]"><Loader2 className="animate-spin inline" size={14}/></div>}
      </div>

      <ParkFormDialog
        park={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />

      <CsvImportDialog
        open={importing}
        onClose={() => setImporting(false)}
        onComplete={() => load()}
        title="Bulk import Parks"
        endpoint="/assets/parks/import"
        templateFilename="parks-template.csv"
        templateHeaders={["name", "operator_name", "address_line", "town", "county", "postcode", "manager_name", "manager_phone", "manager_email", "status"]}
        templateExampleRow={["Riverside Park", "Sunny Living Group", "12 Lakeside Drive", "Colchester", "Essex", "CO5 8AB", "Jane Manager", "01234 567890", "jane@sunnyliving.uk", "ACTIVE"]}
        testid="parks-csv-import"
      />
    </div>
  );
}

function ParkThumb({ src }) {
  return (
    <div className="w-16 h-12 rounded overflow-hidden bg-[#F7F4ED] border border-[#E2DCD0] flex items-center justify-center">
      {src
        ? <img src={`${process.env.REACT_APP_BACKEND_URL}${src}`} alt="" className="w-full h-full object-cover"/>
        : <Trees size={16} className="text-[#94A3B8]"/>}
    </div>
  );
}

function StatusPill({ status }) {
  const s = (status || "ACTIVE").toUpperCase();
  const tone = s === "ACTIVE" ? "bg-[#064E2E]/10 text-[#064E2E]" : "bg-[#475569]/10 text-[#475569]";
  return <span className={`text-[10px] uppercase tracking-[0.12em] font-semibold px-2 py-1 rounded ${tone}`}>{s}</span>;
}

/* Park create/edit dialog */
export function ParkFormDialog({ park, onClose, onSaved }) {
  const isEdit = park && park.id;
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!park) return;
    setDraft({
      name: park.name || "",
      operator_name: park.operator_name || "",
      address_line: park.address_line || "",
      town: park.town || "",
      county: park.county || "",
      postcode: park.postcode || "",
      manager_name: park.manager_name || "",
      manager_phone: park.manager_phone || "",
      manager_email: park.manager_email || "",
      photo_upload_id: park.photo_upload_id || "",
      notes: park.notes || "",
      status: park.status || "ACTIVE",
    });
  }, [park]);

  const save = async () => {
    if (!draft.name?.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      if (isEdit) {
        await adminApi.patch(`/assets/parks/${park.id}`, draft);
      } else {
        await adminApi.post("/assets/parks", draft);
      }
      toast.success(isEdit ? "Park updated" : "Park created");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <FormDialog
      open={!!park}
      onClose={onClose}
      title={isEdit ? `Edit · ${park?.name || "Park"}` : "Add Park"}
      onSubmit={save}
      busy={busy}
      submitLabel={isEdit ? "Save changes" : "Create park"}
      testid="park-form-dialog"
    >
      <PhotoUploader value={draft.photo_upload_id} onChange={(v) => setDraft({ ...draft, photo_upload_id: v })} label="Park photo" testid="park-photo-upload"/>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Park name *" full>
          <input className="field-input m-0 h-10" value={draft.name || ""} onChange={(e) => setDraft({ ...draft, name: e.target.value })} required data-testid="park-name-input"/>
        </Field>
        <Field label="Operator">
          <input className="field-input m-0 h-10" value={draft.operator_name || ""} onChange={(e) => setDraft({ ...draft, operator_name: e.target.value })} data-testid="park-operator-input"/>
        </Field>
        <Field label="Status">
          <select className="field-input m-0 h-10" value={draft.status} onChange={(e) => setDraft({ ...draft, status: e.target.value })} data-testid="park-status-input">
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
          </select>
        </Field>
        <Field label="Address" full>
          <input className="field-input m-0 h-10" value={draft.address_line || ""} onChange={(e) => setDraft({ ...draft, address_line: e.target.value })} data-testid="park-address-input"/>
        </Field>
        <Field label="Town">
          <input className="field-input m-0 h-10" value={draft.town || ""} onChange={(e) => setDraft({ ...draft, town: e.target.value })}/>
        </Field>
        <Field label="County">
          <input className="field-input m-0 h-10" value={draft.county || ""} onChange={(e) => setDraft({ ...draft, county: e.target.value })}/>
        </Field>
        <Field label="Postcode">
          <input className="field-input m-0 h-10" value={draft.postcode || ""} onChange={(e) => setDraft({ ...draft, postcode: e.target.value })} data-testid="park-postcode-input"/>
        </Field>
        <Field label="Park manager">
          <input className="field-input m-0 h-10" value={draft.manager_name || ""} onChange={(e) => setDraft({ ...draft, manager_name: e.target.value })} data-testid="park-manager-input"/>
        </Field>
        <Field label="Manager phone">
          <input className="field-input m-0 h-10" value={draft.manager_phone || ""} onChange={(e) => setDraft({ ...draft, manager_phone: e.target.value })}/>
        </Field>
        <Field label="Manager email">
          <input type="email" className="field-input m-0 h-10" value={draft.manager_email || ""} onChange={(e) => setDraft({ ...draft, manager_email: e.target.value })}/>
        </Field>
        <Field label="Notes" full>
          <textarea rows={2} className="field-input m-0 py-2" value={draft.notes || ""} onChange={(e) => setDraft({ ...draft, notes: e.target.value })}/>
        </Field>
      </div>
    </FormDialog>
  );
}
